import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
  HubConnectionState,
} from '@microsoft/signalr';

import { Subscription } from '@libs/subscription';

import { FUNC_NOOP } from './constants';

const SOCKET_PING_INTERVAL = 15 * 1000;
const SOCKET_TIMEOUT_MS = 30 * 1000;

// lifecycle сокета
export type SocketInternalEvents =
  | { eventType: 'SocketReconnecting' }
  | { eventType: 'SocketReconnected' }
  | { eventType: 'SocketConnected' }
  | { eventType: 'SocketClosed' };

export type SocketOptions<T> = {
  accessTokenFactory?: () => string | Promise<string>;
  onMsg?: (e: Exclude<T, SocketInternalEvents>) => void;
  pingMs?: number;
  timeoutMs?: number;
};

export class SignalRSocket<T> {
  connection: HubConnection;
  discarded = false;
  #subscription = new Subscription<T | SocketInternalEvents>();
  #abortController = new AbortController();

  protected constructor(
    public url: string,
    eventTypes: string[],
    {
      accessTokenFactory,
      onMsg,
      pingMs = SOCKET_PING_INTERVAL,
      timeoutMs = SOCKET_TIMEOUT_MS,
    }: SocketOptions<T> = {},
  ) {
    this.connection = new HubConnectionBuilder()
      .withUrl(url, {
        withCredentials: false,
        transport: HttpTransportType.WebSockets,
        skipNegotiation: true,
        accessTokenFactory,
      })
      .configureLogging(LogLevel.Error)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (e) => this.#calcRetryTimeout(e.previousRetryCount),
      })
      .build();

    this.connection.keepAliveIntervalInMilliseconds = pingMs;
    this.connection.serverTimeoutInMilliseconds = timeoutMs;

    eventTypes.forEach((type) => {
      this.connection.on(type, (e: Exclude<T, SocketInternalEvents>) => {
        onMsg?.(e);
        this.#subscription.publish(e);
      });
    });

    this.connection.onreconnecting(() => {
      this.#subscription.publish({ eventType: 'SocketReconnecting' });
    });

    this.connection.onreconnected(() => {
      this.#subscription.publish({ eventType: 'SocketReconnected' });
    });

    this.connection.onclose(() => {
      this.#subscription.publish({ eventType: 'SocketClosed' });
    });

    addEventListener(
      'visibilitychange',
      () => !document.hidden && this.wasUbableToConnect && this.#start(),
      { signal: this.#abortController.signal },
    );

    addEventListener('online', () => this.wasUbableToConnect && this.#start(), {
      signal: this.#abortController.signal,
    });

    this.#start();
  }

  get subscribers() {
    return this.#subscription.size;
  }

  get wasUbableToConnect() {
    return !this.discarded && this.connection.state === HubConnectionState.Disconnected;
  }

  subscribe(cb: (e: T | SocketInternalEvents) => void): () => void {
    if (this.discarded) return FUNC_NOOP;

    return this.#subscription.subscribe(cb);
  }

  async discard() {
    if (this.discarded) return;

    this.discarded = true;
    this.#abortController.abort();
    await this.connection.stop();
    this.#subscription.clear();
  }

  #start(retry = 0) {
    if (this.discarded) return;

    this.connection.start().then(
      () => {
        this.#subscription.publish(
          retry ? { eventType: 'SocketReconnected' } : { eventType: 'SocketConnected' },
        );
      },
      () => {
        if (this.discarded) return;

        if (retry === 0) {
          this.#subscription.publish({ eventType: 'SocketReconnecting' });
        }

        // SignalR не пытается переподключиться автоматически, если самое первое
        // соединение не смогло подключиться. Поэтому, запускаем переподключение
        // вручную

        const delay = this.#calcRetryTimeout(retry);

        if (delay !== null) {
          window.setTimeout(() => {
            this.#start(retry + 1);
          }, delay);
        } else {
          this.#subscription.publish({ eventType: 'SocketClosed' });
        }
      },
    );
  }

  #calcRetryTimeout(retry: number) {
    if (retry === 0) return 0.5 * 1000;
    if (retry === 1) return 1 * 1000;
    if (retry === 2) return 3 * 1000;

    // 5 попыток в фоне, ~12,5 сек (sic! :)
    if (retry === 5 && document.hidden) return null;

    if (retry <= 10) return 5 * 1000;
    if (retry <= 100) return 10 * 1000;

    return null;
  }
}
